import { useState, useEffect, useCallback } from 'react'
import { X, Zap, CheckCircle, ArrowRight, Users, Lock, Sparkles, Mail, User, Rocket, Star, Crown } from 'lucide-react'

// ── Constants ─────────────────────────────────────────────────────────────────
const LAUNCH_DATE   = new Date('2026-06-20T00:00:00')
const TOTAL_SPOTS   = 100
const CLAIMED_SPOTS = 63

const PLANS = {
  starter: {
    label: 'Starter', price: 24.50, original: 49,
    Icon: Rocket, color: '#60a5fa',
    features: ['1 Trading Bot', '5 Strategies', 'Email Alerts'],
  },
  pro: {
    label: 'Pro', price: 49.50, original: 99,
    Icon: Star, color: '#a78bfa',
    features: ['5 Trading Bots', '20 Strategies', 'Real-time Alerts', 'API Access'],
  },
  elite: {
    label: 'Elite', price: 124.50, original: 249,
    Icon: Crown, color: '#fbbf24',
    features: ['Unlimited Bots', 'All Strategies', 'Priority Support', 'Custom APIs', 'Dedicated Manager'],
  },
}

function getRemaining() {
  const diff = LAUNCH_DATE - new Date()
  if (diff <= 0) return { d: 0, h: 0, m: 0, s: 0 }
  return {
    d: Math.floor(diff / 86400000),
    h: Math.floor((diff % 86400000) / 3600000),
    m: Math.floor((diff % 3600000) / 60000),
    s: Math.floor((diff % 60000) / 1000),
  }
}

const pad = (n) => String(n).padStart(2, '0')

// ── Countdown block ───────────────────────────────────────────────────────────
function CountdownBlock({ value, label }) {
  return (
    <div className="flex flex-col items-center gap-1.5">
      <div
        className="w-14 sm:w-16 h-14 sm:h-16 rounded-2xl flex items-center justify-center font-black text-xl sm:text-2xl text-white tabular-nums select-none relative overflow-hidden"
        style={{
          background: 'linear-gradient(145deg, rgba(124,58,237,0.35) 0%, rgba(79,70,229,0.25) 100%)',
          border: '1px solid rgba(124,58,237,0.55)',
          boxShadow: '0 0 20px rgba(124,58,237,0.35), inset 0 1px 0 rgba(255,255,255,0.12)',
        }}
      >
        {/* inner shimmer line */}
        <div className="absolute inset-x-0 top-0 h-px" style={{ background: 'linear-gradient(90deg,transparent,rgba(255,255,255,0.25),transparent)' }} />
        {pad(value)}
      </div>
      <span className="text-[10px] font-bold tracking-widest uppercase" style={{ color: 'rgba(167,139,250,0.7)' }}>{label}</span>
    </div>
  )
}

// ── Main modal ────────────────────────────────────────────────────────────────
export default function EarlyAccessModal({ open, onClose, initialPlan = 'pro' }) {
  const [time,  setTime]  = useState(getRemaining)
  const [name,  setName]  = useState('')
  const [email, setEmail] = useState('')
  const [plan,  setPlan]  = useState(initialPlan)
  const [step,  setStep]  = useState('form')   // 'form' | 'loading' | 'success'
  const [error, setError] = useState('')

  // Sync plan when parent changes it
  useEffect(() => { setPlan(initialPlan) }, [initialPlan])

  // Countdown tick
  useEffect(() => {
    const t = setInterval(() => setTime(getRemaining()), 1000)
    return () => clearInterval(t)
  }, [])

  // Body scroll lock
  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
      // Reset form when closed
      const timer = setTimeout(() => { setStep('form'); setError('') }, 300)
      return () => clearTimeout(timer)
    }
    return () => { document.body.style.overflow = '' }
  }, [open])

  // ESC key close
  useEffect(() => {
    if (!open) return
    const onKey = (e) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onClose])

  const handleBackdrop = useCallback((e) => {
    if (e.target === e.currentTarget) onClose()
  }, [onClose])

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!name.trim() || !email.trim()) { setError('Please fill in all fields'); return }
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) { setError('Please enter a valid email'); return }
    setError('')
    setStep('loading')
    try {
      const res = await fetch('/.netlify/functions/save-lead', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ name: name.trim(), email: email.trim(), plan }),
      })
      const data = await res.json()
      if (!res.ok) throw new Error(data.error || 'Something went wrong')
      setStep('success')
    } catch (err) {
      setError(err.message || 'Could not save your spot. Please try again.')
      setStep('form')
    }
  }

  const handleCheckout = async () => {
    try {
      const res = await fetch('/.netlify/functions/create-checkout', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ plan, name: name.trim(), email: email.trim() }),
      })
      const data = await res.json()
      if (data.url) window.location.href = data.url
    } catch (err) {
      console.error('Checkout error:', err)
    }
  }

  const selectedPlan = PLANS[plan]
  const spotsLeft    = TOTAL_SPOTS - CLAIMED_SPOTS

  if (!open) return null

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center p-4"
      style={{ background: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(10px)' }}
      onClick={handleBackdrop}
    >
      {/* Glow behind modal */}
      <div
        className="absolute pointer-events-none"
        style={{
          width: '500px', height: '300px',
          background: 'radial-gradient(ellipse, rgba(124,58,237,0.35) 0%, transparent 70%)',
          filter: 'blur(40px)',
          top: '50%', left: '50%', transform: 'translate(-50%,-60%)',
        }}
      />

      <div
        className="relative w-full max-w-md overflow-hidden"
        style={{
          background:    'rgba(7,5,20,0.98)',
          border:        '1px solid rgba(124,58,237,0.4)',
          borderRadius:  '22px',
          boxShadow:     '0 0 80px rgba(124,58,237,0.25), 0 40px 80px rgba(0,0,0,0.6)',
          backdropFilter:'blur(20px)',
          animation:     'slideUp 0.35s cubic-bezier(0.22,1,0.36,1)',
        }}
      >
        {/* Top gradient bar */}
        <div style={{ height: '3px', background: 'linear-gradient(90deg,#7c3aed,#06b6d4,#a78bfa,#7c3aed)', backgroundSize: '200% 100%', animation: 'gradientShift 4s linear infinite' }} />

        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-1.5 rounded-lg text-gray-500 hover:text-white hover:bg-white/10 transition-all z-10"
          aria-label="Close"
        >
          <X size={17} />
        </button>

        <div className="p-6 sm:p-8">
          {/* ── FORM STEP ── */}
          {(step === 'form' || step === 'loading') && (
            <>
              {/* Badge */}
              <div className="flex justify-center mb-5">
                <span
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs font-black tracking-wider text-white"
                  style={{
                    background: 'linear-gradient(135deg,rgba(124,58,237,0.9),rgba(79,70,229,0.9))',
                    boxShadow: '0 0 24px rgba(124,58,237,0.55), inset 0 1px 0 rgba(255,255,255,0.15)',
                    border: '1px solid rgba(167,139,250,0.3)',
                  }}
                >
                  <Zap size={13} fill="white" stroke="white" />
                  EARLY ACCESS — 50% OFF FOREVER
                </span>
              </div>

              {/* Headline */}
              <h2 className="text-center text-white font-black text-2xl sm:text-3xl leading-tight mb-1">
                Platform launches in
              </h2>
              <p className="text-center text-gray-500 text-sm mb-5">June 20, 2026 — lock in your price today</p>

              {/* Countdown */}
              <div className="flex items-center justify-center gap-2 sm:gap-3 mb-5">
                <CountdownBlock value={time.d} label="Days" />
                <span className="text-violet-500 font-black text-2xl pb-4">:</span>
                <CountdownBlock value={time.h} label="Hrs" />
                <span className="text-violet-500 font-black text-2xl pb-4">:</span>
                <CountdownBlock value={time.m} label="Min" />
                <span className="text-violet-500 font-black text-2xl pb-4">:</span>
                <CountdownBlock value={time.s} label="Sec" />
              </div>

              {/* Spots progress */}
              <div className="mb-5 rounded-2xl p-3.5" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
                <div className="flex items-center justify-between text-xs mb-2.5">
                  <span className="flex items-center gap-2 font-semibold" style={{ color: 'rgba(167,139,250,0.9)' }}>
                    <span className="flex items-center justify-center w-5 h-5 rounded-md" style={{ background: 'rgba(124,58,237,0.3)', border: '1px solid rgba(124,58,237,0.5)' }}>
                      <Users size={11} style={{ color: '#a78bfa' }} />
                    </span>
                    Early access spots
                  </span>
                  <span className="font-black text-white">{CLAIMED_SPOTS}/{TOTAL_SPOTS} claimed</span>
                </div>
                <div className="w-full h-2 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.07)' }}>
                  <div
                    className="h-full rounded-full"
                    style={{
                      width: `${(CLAIMED_SPOTS / TOTAL_SPOTS) * 100}%`,
                      background: 'linear-gradient(90deg,#7c3aed,#06b6d4)',
                      boxShadow: '0 0 8px rgba(124,58,237,0.6)',
                    }}
                  />
                </div>
                <p className="text-xs font-bold mt-2 flex items-center gap-1.5" style={{ color: '#fb923c' }}>
                  <Zap size={11} fill="#fb923c" stroke="none" />
                  Only {spotsLeft} spots remaining at 50% off
                </p>
              </div>

              {/* Plan selector */}
              <div className="grid grid-cols-3 gap-2 mb-5">
                {Object.entries(PLANS).map(([key, p]) => {
                  const active = plan === key
                  const PlanIcon = p.Icon
                  return (
                    <button
                      key={key}
                      onClick={() => setPlan(key)}
                      className="rounded-2xl p-3 text-center transition-all cursor-pointer"
                      style={{
                        background: active ? `rgba(${key === 'starter' ? '96,165,250' : key === 'pro' ? '124,58,237' : '251,191,36'},0.15)` : 'rgba(255,255,255,0.03)',
                        border: `1.5px solid ${active ? p.color : 'rgba(255,255,255,0.07)'}`,
                        boxShadow: active ? `0 0 18px ${p.color}44` : 'none',
                        transform: active ? 'scale(1.04)' : 'scale(1)',
                      }}
                    >
                      {key === 'pro' && (
                        <p className="text-[9px] font-black mb-1 uppercase tracking-wider" style={{ color: '#22d3ee' }}>Popular</p>
                      )}
                      <div className="flex justify-center mb-1.5">
                        <span
                          className="w-7 h-7 rounded-lg flex items-center justify-center"
                          style={{
                            background: active ? `${p.color}22` : 'rgba(255,255,255,0.05)',
                            border: `1px solid ${active ? p.color + '55' : 'rgba(255,255,255,0.1)'}`,
                          }}
                        >
                          <PlanIcon size={14} style={{ color: active ? p.color : 'rgba(255,255,255,0.35)' }} />
                        </span>
                      </div>
                      <p className="text-white text-xs font-bold leading-none mb-1">{p.label}</p>
                      <p className="text-[10px] line-through leading-none" style={{ color: 'rgba(255,255,255,0.25)' }}>${p.original}/mo</p>
                      <p className="text-xs font-black leading-none mt-0.5" style={{ color: active ? p.color : 'rgba(167,139,250,0.6)' }}>${p.price}/mo</p>
                    </button>
                  )
                })}
              </div>

              {/* Form */}
              <form onSubmit={handleSubmit} className="space-y-3">
                {/* Name input */}
                <div className="relative group">
                  <span
                    className="absolute left-3.5 top-1/2 -translate-y-1/2 flex items-center justify-center w-6 h-6 rounded-md transition-all"
                    style={{ background: 'rgba(124,58,237,0.2)', border: '1px solid rgba(124,58,237,0.3)' }}
                  >
                    <User size={12} style={{ color: '#a78bfa' }} />
                  </span>
                  <input
                    type="text"
                    placeholder="Your full name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    disabled={step === 'loading'}
                    className="w-full pl-12 pr-4 py-3 rounded-xl text-white text-sm placeholder-gray-600 outline-none transition-all disabled:opacity-50"
                    style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}
                    onFocus={(e) => { e.target.style.borderColor = 'rgba(124,58,237,0.7)'; e.target.style.boxShadow = '0 0 0 3px rgba(124,58,237,0.15)' }}
                    onBlur={(e) => { e.target.style.borderColor = 'rgba(255,255,255,0.1)'; e.target.style.boxShadow = 'none' }}
                  />
                </div>
                {/* Email input */}
                <div className="relative group">
                  <span
                    className="absolute left-3.5 top-1/2 -translate-y-1/2 flex items-center justify-center w-6 h-6 rounded-md transition-all"
                    style={{ background: 'rgba(6,182,212,0.15)', border: '1px solid rgba(6,182,212,0.3)' }}
                  >
                    <Mail size={12} style={{ color: '#22d3ee' }} />
                  </span>
                  <input
                    type="email"
                    placeholder="Your email address"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={step === 'loading'}
                    className="w-full pl-12 pr-4 py-3 rounded-xl text-white text-sm placeholder-gray-600 outline-none transition-all disabled:opacity-50"
                    style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}
                    onFocus={(e) => { e.target.style.borderColor = 'rgba(6,182,212,0.6)'; e.target.style.boxShadow = '0 0 0 3px rgba(6,182,212,0.12)' }}
                    onBlur={(e) => { e.target.style.borderColor = 'rgba(255,255,255,0.1)'; e.target.style.boxShadow = 'none' }}
                  />
                </div>
                {error && (
                  <p className="text-red-400 text-xs text-center bg-red-500/10 rounded-lg py-2 px-3">{error}</p>
                )}
                <button
                  type="submit"
                  disabled={step === 'loading'}
                  className="w-full py-3.5 rounded-xl text-white font-black text-sm transition-all hover:opacity-90 hover:-translate-y-0.5 flex items-center justify-center gap-2 group disabled:opacity-60"
                  style={{
                    background: 'linear-gradient(135deg,#7c3aed,#4f46e5)',
                    boxShadow: '0 0 32px rgba(124,58,237,0.55), 0 4px 16px rgba(0,0,0,0.3)',
                  }}
                >
                  {step === 'loading' ? (
                    <>
                      <span className="w-4 h-4 rounded-full border-2 border-white/40 border-t-white animate-spin" />
                      Securing your spot...
                    </>
                  ) : (
                    <>
                      <Sparkles size={15} />
                      Claim My 50% Off — {selectedPlan.label}
                      <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
                    </>
                  )}
                </button>
              </form>

              <div
                className="flex items-center justify-center gap-2 mt-3 px-3 py-2 rounded-lg"
                style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}
              >
                <span className="flex items-center justify-center w-4 h-4 rounded-md" style={{ background: 'rgba(74,222,128,0.15)', border: '1px solid rgba(74,222,128,0.3)' }}>
                  <Lock size={9} style={{ color: '#4ade80' }} />
                </span>
                <p className="text-[11px] font-medium" style={{ color: 'rgba(156,163,175,0.8)' }}>No credit card required • Join the free waitlist</p>
              </div>
            </>
          )}

          {/* ── SUCCESS STEP ── */}
          {step === 'success' && (
            <div className="flex flex-col items-center text-center gap-4 py-2">
              {/* Success icon */}
              <div
                className="w-20 h-20 rounded-full flex items-center justify-center"
                style={{
                  background:  'rgba(74,222,128,0.12)',
                  border:      '2px solid rgba(74,222,128,0.4)',
                  boxShadow:   '0 0 40px rgba(74,222,128,0.25)',
                  animation:   'pulse 2s ease-in-out infinite',
                }}
              >
                <CheckCircle size={36} className="text-green-400" />
              </div>

              <div>
                <h3 className="text-white text-2xl font-black mb-1">You're on the list! 🎉</h3>
                <p className="text-gray-400 text-sm leading-relaxed">
                  Check your inbox — your exclusive 50% discount<br />
                  code is on its way to <span className="text-violet-300 font-medium">{email}</span>
                </p>
              </div>

              {/* Plan summary */}
              <div
                className="w-full rounded-xl p-4 text-left"
                style={{ background: 'rgba(124,58,237,0.1)', border: '1px solid rgba(124,58,237,0.25)' }}
              >
                <p className="text-violet-400 text-[11px] font-black uppercase tracking-wider mb-2">Your locked-in plan</p>
                <div className="flex items-center justify-between">
                  <span className="text-white font-bold text-lg">{selectedPlan.label} Plan</span>
                  <div className="text-right">
                    <span className="text-gray-600 line-through text-sm">${selectedPlan.original}/mo</span>
                    <span className="text-green-400 font-black text-xl ml-2">${selectedPlan.price}/mo</span>
                  </div>
                </div>
                <p className="text-gray-500 text-xs mt-1">Locked in forever • Starts June 20, 2026</p>
              </div>

              {/* Checkout CTA */}
              <div className="w-full space-y-2">
                <button
                  onClick={handleCheckout}
                  className="w-full py-3.5 rounded-xl text-white font-black text-sm transition-all hover:opacity-90 hover:-translate-y-0.5 flex items-center justify-center gap-2 group"
                  style={{ background: 'linear-gradient(135deg,#7c3aed,#4f46e5)', boxShadow: '0 0 30px rgba(124,58,237,0.5)' }}
                >
                  Secure My Spot Now (${selectedPlan.price})
                  <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
                </button>
                <button
                  onClick={onClose}
                  className="w-full py-2 text-gray-500 hover:text-gray-300 text-xs transition-colors"
                >
                  I'll do this later
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Bottom gradient bar */}
        <div style={{ height: '1px', background: 'linear-gradient(90deg,transparent,rgba(124,58,237,0.3),transparent)' }} />
      </div>

      {/* Animations */}
      <style>{`
        @keyframes slideUp {
          from { opacity: 0; transform: translateY(32px) scale(0.96); }
          to   { opacity: 1; transform: translateY(0)     scale(1); }
        }
        @keyframes gradientShift {
          0%   { background-position: 0% 50%; }
          100% { background-position: 200% 50%; }
        }
      `}</style>
    </div>
  )
}
